import {
  Button,
  Checkbox,
  CheckboxWrapper,
  InputField,
  TextField,
} from '@design-system';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { GIFTCARD_PAGE_TYPE } from '../../constants';
import { DEFAULT_CURRENCY_CODE } from '../../constants/locations';
import AvantArteAuthGateway from '../../gateways/AvantArteAuthGateway';
import { emailFor } from '../../helpers/links';
import { useGiftCardPurchasing } from '../../hooks/useGiftCardPurchasing';
import { GiftCardCheckoutPayload } from '../../types/GiftCard';
import {
  CheckoutGateway,
  GiftCardVariantProduct,
  ShopifyProductPurchaseDetails,
} from '../../types/Purchasing';
import { TrackPageView } from '../../utils/analytics';
import { renderStoryblokRichText } from '../../utils/renderStoryblokRichText';
import GiftCardBanner from '../Content/GiftCardBanner';
import PageSection from '../Layout/PageSection';
import UnderlinedLink from '../UnderlinedLink';

type Props = {
  blok: GiftCardPageType;
  giftCardVariantProducts: Array<GiftCardVariantProduct>;
  checkoutGateway: CheckoutGateway<
    ShopifyProductPurchaseDetails,
    GiftCardCheckoutPayload
  >;
  authGateway: AvantArteAuthGateway;
};

const GiftCards: React.FC<Props> = ({
  authGateway,
  blok,
  checkoutGateway,
  giftCardVariantProducts,
}) => {
  const { t } = useTranslation('giftCards');
  const form = useForm({
    defaultValues: {
      giftCardVariantId: null,
      recipientName: null,
      senderName: null,
      message: null,
    } as GiftCardCheckoutPayload,
  });

  const variantId = form.watch('giftCardVariantId');

  const getInputValidation = (
    formFieldNameOne: keyof GiftCardCheckoutPayload,
    formFieldNameTwo: keyof GiftCardCheckoutPayload,
    value: string | null,
  ) => {
    if (
      form.getValues()[formFieldNameOne] ||
      form.getValues()[formFieldNameTwo]
    ) {
      return !(value === null || value.length === 0);
    }
    if (
      !form.getValues()[formFieldNameOne] &&
      form.getValues()[formFieldNameTwo]
    ) {
      return value !== null && value.length !== 0;
    }
    return true;
  };

  const purchasingController = useGiftCardPurchasing({
    checkoutGateway,
    authGateway,
    reset: form.reset,
  });

  useEffect(() => {
    // add error tracking here
    TrackPageView({ page: GIFTCARD_PAGE_TYPE });
  }, []);

  const handleSubmit = form.handleSubmit((d) => {
    purchasingController.goToCheckout({
      ...d,
      giftCardVariantId: (d.giftCardVariantId as string).replace('_mobile', ''),
      giftCardValue: giftCardVariantProducts.filter(
        (giftCard: GiftCardVariantProduct) =>
          d.giftCardVariantId &&
          giftCard.variantId === d.giftCardVariantId.replace('_mobile', ''),
      )[0].price,
      imageCredentialsTitle: blok.giftCardBanner[0].imageCredentialsTitle,
      imageCredentialsDescription:
        blok.giftCardBanner[0].imageCredentialsDescription,
    });
    form.reset({
      giftCardVariantId: null,
      recipientName: null,
      senderName: null,
      message: null,
    });
  });
  return (
    <main className="my-9 space-y-9 md:my-12 md:space-y-12">
      <PageSection>
        <div className="mx-auto flex w-full max-w-lg flex-col text-center">
          <div className="w-full flex-1 space-y-2">
            <h1 className="text-center text-md font-bold md:text-lg">
              {blok.title}
            </h1>
            <div className="text-center text-md">
              {renderStoryblokRichText(blok.subtitle, {
                paragraphClassName: 'pb-0',
              })}
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection>
        <GiftCardBanner
          analytics={{
            page: GIFTCARD_PAGE_TYPE,
            pageTitle: blok.title,
            context: 'banner',
          }}
          blok={blok.giftCardBanner[0]}
          exampleGiftCard={blok.exampleGiftCard}
        />
      </PageSection>

      <PageSection>
        <div className="mx-auto flex w-full max-w-md flex-col gap-9 md:gap-12">
          <div className="flex w-full flex-col gap-4">
            <h2 className="text-md font-bold">{t('howDoesItWork')}</h2>

            {blok.instructions.map((instruction: Paragraph, i: number) => (
              <div key={i}>
                <p className="font-bold">{instruction.title}</p>
                {renderStoryblokRichText(instruction.text, {
                  paragraphClassName: '',
                })}
              </div>
            ))}

            <p className="text-neutral-4">
              {t('faqMoreInfo')}
              <Link className="hyperlink" href="/support/gift-cards">
                {t('faqMoreInfoLink')}
              </Link>
              .
            </p>
          </div>

          <form
            className="flex w-full flex-col gap-9 md:gap-12"
            onSubmit={handleSubmit}
          >
            <div className="flex w-full flex-col gap-4">
              <h3 className="text-md font-bold">{t('giftCardValue.title')}</h3>

              <CheckboxWrapper
                error={
                  form.formState.errors.giftCardVariantId?.type === 'required'
                    ? t('giftCardValue.selectWarning')
                    : undefined
                }
                label={t('giftCardValue.description')}
                note={
                  <Trans i18nKey="giftCardValue.hint" t={t}>
                    Note: Want to add a different value or use another currency?
                    <UnderlinedLink
                      href={emailFor({ email: 'COLLECTING' })}
                      target="_blank"
                    >
                      Get in touch
                    </UnderlinedLink>
                  </Trans>
                }
              >
                {giftCardVariantProducts.map(
                  (giftCard: GiftCardVariantProduct) => (
                    <Checkbox
                      id={giftCard.variantId}
                      type="radio"
                      {...form.register('giftCardVariantId', {
                        required: {
                          value: true,
                          message: t('validation.checkbox'),
                        },
                      })}
                      key={giftCard.variantId}
                      checked={variantId === giftCard.variantId}
                      disabled={purchasingController.checkoutLoading}
                      label={new Intl.NumberFormat('de-DE', {
                        style: 'currency',
                        currency: DEFAULT_CURRENCY_CODE,
                        minimumFractionDigits: 0,
                      }).format(parseInt(giftCard.price, 10))}
                      value={giftCard.variantId}
                    />
                  ),
                )}
              </CheckboxWrapper>
            </div>

            <div className="flex w-full flex-col gap-4">
              <h3 className="text-md font-bold">
                {t('personalization.title')}
              </h3>

              <InputField
                key="recipientName"
                label={t('personalization.giftToLabel')}
                {...form.register('recipientName', {
                  validate: (value) => {
                    return getInputValidation('senderName', 'message', value);
                  },
                })}
                disabled={purchasingController.checkoutLoading}
                error={
                  form.formState.errors.recipientName &&
                  form.formState.errors.recipientName.type === 'validate'
                    ? t('validation.recipientName')
                    : undefined
                }
                id="recipient"
                placeholder={t('personalization.giftToPlaceholder')}
              />

              <InputField
                key="senderName"
                label={t('personalization.fromLabel')}
                {...form.register('senderName', {
                  validate: (value) => {
                    return getInputValidation(
                      'recipientName',
                      'message',
                      value,
                    );
                  },
                })}
                disabled={purchasingController.checkoutLoading}
                error={
                  form.formState.errors.senderName &&
                  form.formState.errors.senderName.type === 'validate'
                    ? t('validation.senderName')
                    : undefined
                }
                id="sender"
                placeholder={t('personalization.fromPlaceholder')}
              />

              <TextField
                key="message"
                note={t('personalization.messageNote')}
                {...form.register('message', {
                  max: 150,
                  validate: (value) => {
                    return getInputValidation(
                      'recipientName',
                      'senderName',
                      value,
                    );
                  },
                })}
                disabled={purchasingController.checkoutLoading}
                error={
                  form.formState.errors.message &&
                  form.formState.errors.message.type === 'validate'
                    ? t('validation.message')
                    : undefined
                }
                id="message"
                label={t('personalization.messageLabel')}
                placeholder={t('personalization.messagePlaceholder')}
                rows={3}
              />
            </div>

            <Button
              fullWidth
              label={
                purchasingController.checkoutLoading
                  ? t('checkoutButtonLoadingTitle')
                  : t('checkoutButtonTitle')
              }
              loading={purchasingController.checkoutLoading}
              type="submit"
              variant="primary"
            />
          </form>
        </div>
      </PageSection>
    </main>
  );
};

export default GiftCards;
